import { useState } from 'react';
import { Search, SlidersHorizontal, LayoutGrid, List, CalendarDays } from 'lucide-react';
import { Input } from '../ui/input';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { PIPELINE_FLOW, STAGE_META } from '../../lib/workflow';

export const STAGE_TABS = [
  { id: 'all', label: 'All', emoji: '📁' },
  ...PIPELINE_FLOW.map((s) => ({ id: s, label: STAGE_META[s].label, emoji: STAGE_META[s].emoji })),
  { id: 'none', label: 'Not in pipeline', emoji: '🗂️' },
];

export const DATE_PRESETS = [
  { id: 'all', label: 'All time' },
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last7', label: 'Last 7 days' },
  { id: 'month', label: 'This month' },
];

const pill = (active) => `shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-[background-color,color,transform] duration-150 active:scale-95 ${
  active ? 'bg-[#2D2623] text-white border-[#2D2623]' : 'bg-white text-[#6E635B] border-[#E8E1D5] hover:bg-[#F7F3EC]'
}`;

export const StageTabs = ({ value, onChange, counts }) => (
  <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="listings-stage-tabs">
    {STAGE_TABS.map((t) => (
      <button key={t.id} onClick={() => onChange(t.id)} className={pill(value === t.id)} data-testid={`listings-stage-tab-${t.id}`}>
        <span aria-hidden>{t.emoji}</span>{t.label}
        <span className={`rounded-full px-1.5 text-[10px] ${value === t.id ? 'bg-white/20' : 'bg-[#F7F3EC]'}`} data-testid={`listings-stage-count-${t.id}`}>{counts[t.id] || 0}</span>
      </button>
    ))}
  </div>
);

export const DateBar = ({ preset, onPreset, range, onRange }) => {
  const [open, setOpen] = useState(false);
  const fmt = (d) => d?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="listings-date-bar">
      <span className="text-xs font-bold text-[#9A8E85] shrink-0">📅</span>
      {DATE_PRESETS.map((d) => (
        <button key={d.id} onClick={() => onPreset(d.id)} className={pill(preset === d.id)} data-testid={`listings-date-preset-${d.id}`}>{d.label}</button>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button className={pill(preset === 'custom')} data-testid="listings-date-preset-custom">
            <CalendarDays size={12} />
            {preset === 'custom' && range?.from ? `${fmt(range.from)} – ${fmt(range.to || range.from)}` : 'Custom range'}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0 rounded-2xl" data-testid="listings-date-range-popover">
          <Calendar mode="range" numberOfMonths={2} selected={range} onSelect={(r) => { onRange(r); onPreset('custom'); }} disabled={{ after: new Date() }} />
        </PopoverContent>
      </Popover>
    </div>
  );
};

const FilterSelect = ({ label, value, onChange, options, testId }) => (
  <div className="space-y-1">
    <p className="text-[10px] font-bold uppercase tracking-wider text-[#9A8E85]">{label}</p>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 rounded-xl border-[#E8E1D5] text-xs" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export const SearchFilters = ({ search, onSearch, filters, setFilters, shapes, people, view, onView }) => {
  const active = Object.values(filters).filter((v) => v !== 'all').length;
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  return (
    <div className="flex flex-col sm:flex-row gap-2">
      <div className="relative flex-1">
        <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9A8E85]" />
        <Input value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search listings by name or title…" className="pl-10 h-10 rounded-2xl border-[#E8E1D5] bg-white text-sm" data-testid="etsy-search-input" />
      </div>
      <div className="flex gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <button className="h-10 inline-flex items-center gap-2 rounded-2xl border border-[#E8E1D5] bg-white px-4 text-xs font-bold text-[#2D2623] hover:bg-[#F7F3EC]" data-testid="listings-filters-btn">
              <SlidersHorizontal size={13} /> Filters
              {active > 0 && <span className="rounded-full bg-[#E07A5F] text-white px-1.5 text-[10px]" data-testid="listings-filters-count">{active}</span>}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 rounded-2xl space-y-3" data-testid="listings-filters-popover">
            <FilterSelect label="💎 Shape" value={filters.shape} onChange={set('shape')} testId="etsy-shape-filter" options={[['all', 'All shapes'], ...shapes.map((s) => [s, s])]} />
            <FilterSelect label="🎬 Video" value={filters.video} onChange={set('video')} testId="etsy-video-filter" options={[['all', 'All'], ['with', 'With video'], ['without', 'No video']]} />
            <FilterSelect label="🚫 Not for Etsy" value={filters.nfe} onChange={set('nfe')} testId="etsy-nfe-filter" options={[['all', 'Show all'], ['only', 'Only Not-for-Etsy'], ['hide', 'Hide Not-for-Etsy']]} />
            <FilterSelect label="👤 Submitted by" value={filters.person} onChange={set('person')} testId="etsy-person-filter" options={[['all', 'Everyone'], ...people.map((p) => [p, p])]} />
            {active > 0 && (
              <button onClick={() => setFilters({ shape: 'all', video: 'all', nfe: 'all', person: 'all' })} className="text-xs font-semibold text-[#C85A32] hover:underline" data-testid="etsy-clear-filters">Clear filters</button>
            )}
          </PopoverContent>
        </Popover>
        <div className="flex rounded-2xl border border-[#E8E1D5] bg-white p-1">
          {[['grid', LayoutGrid], ['list', List]].map(([v, Icon]) => (
            <button key={v} onClick={() => onView(v)} className={`w-9 h-8 rounded-xl flex items-center justify-center transition-colors ${view === v ? 'bg-[#2D2623] text-white' : 'text-[#9A8E85] hover:text-[#2D2623]'}`} data-testid={`listings-view-toggle-${v}`} aria-label={`${v} view`}>
              <Icon size={14} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
